import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { AlertTriangle, Car, Plus, X, CheckCircle, MessageSquare } from 'lucide-react';
import { LicensePlate } from './ui/LicensePlate';
import {
  RegistrationModal,
  PhotoSection,
  PhotoCaptureThumb,
  PersonalInfoSection,
  UserLinkingSection,
  UnitAssignmentSection,
} from './registration';
import { PhotoCaptureResult } from './PhotoCaptureModal';
import { PhotoResult } from './PhotoUploadModal';
import { AllPhotosEditModal, PhotoColumn } from './AllPhotosEditModal';
import { formatCPF } from '../utils/cpfFormatter';
import { useKeycloak } from '../contexts/KeycloakContext';
import toast from 'react-hot-toast';
import { FormLoadingOverlay } from './common/FormLoadingOverlay';
import { usePersonFieldConfig } from '../hooks/usePersonFieldConfig';
import { useForceUppercase } from '../hooks/useForceUppercase';

interface Block {
  id: string;
  name: string;
}

interface Unit {
  id: string;
  number: string;
  block_id: string;
}

interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface ResidentFormData {
  first_name: string;
  last_name: string;
  cpf: string;
  rg?: string;
  /** Foreigner identity — see utils/foreigner.ts. */
  is_foreigner?: boolean;
  passport_number?: string;
  /** ISO 3166-1 alpha-2. */
  country?: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  is_minor?: boolean;
}

interface ResidentRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    formData: ResidentFormData;
    photo: PhotoCaptureResult | null;
    documentFrontPhoto: PhotoResult | null;
    documentBackPhoto: PhotoResult | null;
    vehiclePhoto: PhotoResult | null;
    vehicleList: Array<{ plate: string; model: string; color: string }>;
    note: string;
    selectedUser: KeycloakUser | null;
    unitAssignment: {
      unitId: string;
      relationType: string;
      parentesco?: string;
      startDate: string;
    } | null;
  }) => Promise<void>;
  blocks: Block[];
  units: Unit[];
  onBlockChange: (blockId: string) => void;
  editingResident?: any;
  isLoading?: boolean;
  darkMode?: boolean;
  initialBlockId?: string;
  initialUnitId?: string;
  customerId?: string;
  embedded?: boolean; // When true, renders without modal overlay for main frame display
  condoType?: string;
}

export const ResidentRegistrationModal: React.FC<ResidentRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  blocks,
  units,
  onBlockChange,
  editingResident,
  isLoading = false,
  darkMode = false,
  initialBlockId,
  initialUnitId,
  customerId,
  embedded = false,
  condoType,
}) => {
  const fieldConfig = usePersonFieldConfig(customerId, 'resident');
  const [photo, setPhoto] = useState<PhotoCaptureResult | null>(null);
  const [documentFrontPhoto, setDocumentFrontPhoto] = useState<PhotoResult | null>(null);
  const [documentBackPhoto, setDocumentBackPhoto] = useState<PhotoResult | null>(null);
  const [vehiclePhoto, setVehiclePhoto] = useState<PhotoResult | null>(null);
  const [showAllPhotosModal, setShowAllPhotosModal] = useState(false);
  // Which single column to show when opened from a doc/vehicle tile — null means
  // "Editar todas" was clicked, so AllPhotosEditModal falls back to its own DEFAULT_COLUMNS.
  const [allPhotosModalColumn, setAllPhotosModalColumn] = useState<PhotoColumn | null>(null);
  const [selectedUser, setSelectedUser] = useState<KeycloakUser | null>(null);

  // Vehicle — same "add one vehicle inline" pattern as VisitorRegistrationModal
  const [vehicleList, setVehicleList] = useState<Array<{ plate: string; model: string; color: string }>>([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [newVehiclePlate, setNewVehiclePlate] = useState('');
  const [newVehicleModel, setNewVehicleModel] = useState('');
  const [newVehicleColor, setNewVehicleColor] = useState('');

  // Internal note — same pattern as VisitorRegistrationModal's "Nota Interna"
  const [newResidentNote, setNewResidentNote] = useState('');

  // Unit assignment state
  const [selectedBlockId, setSelectedBlockId] = useState(initialBlockId || '');
  const [selectedUnitId, setSelectedUnitId] = useState(initialUnitId || '');
  const [relationType, setRelationType] = useState('');
  const [parentesco, setParentesco] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);
  const [isActuallySubmitting, setIsActuallySubmitting] = useState(false);

  // ESC key double-press state
  const [escPressCount, setEscPressCount] = useState(0);
  const escTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Get operator name and role from Keycloak
  const { user, hasAnyRole } = useKeycloak();
  const operatorName = user?.name || user?.username || 'Operador';
  // Síndico and supervisor manage their own condo — CPF is optional for them
  const isCpfRequired = !hasAnyRole('sindico', 'supervisor');

  const forceUpper = useForceUppercase();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<ResidentFormData>();

  // Reset form when modal opens/closes or editing resident changes
  useEffect(() => {
    if (isOpen) {
      if (editingResident) {
        reset({
          first_name: editingResident.first_name || '',
          last_name: editingResident.last_name || '',
          cpf: editingResident.cpf || '',
          rg: editingResident.rg || '',
          email: editingResident.email || '',
          phone: editingResident.phone || '',
          birth_date: editingResident.date_of_birth || '',
          is_minor: editingResident.is_minor || false,
          is_foreigner: !!editingResident.is_foreigner,
          passport_number: editingResident.passport_number || '',
          country: editingResident.country || '',
        });
      } else {
        reset({
          first_name: '',
          last_name: '',
          cpf: '',
          rg: '',
          email: '',
          phone: '',
          birth_date: '',
          is_minor: false,
          is_foreigner: false,
          passport_number: '',
          country: '',
        });
        setPhoto(null);
        setDocumentFrontPhoto(null);
        setDocumentBackPhoto(null);
        setVehiclePhoto(null);
        setVehicleList([]);
        setShowVehicleForm(false);
        setNewVehiclePlate('');
        setNewVehicleModel('');
        setNewVehicleColor('');
        setNewResidentNote('');
        setShowAllPhotosModal(false);
        setAllPhotosModalColumn(null);
        setSelectedUser(null);
        setSelectedBlockId(initialBlockId || '');
        setSelectedUnitId(initialUnitId || '');
        setRelationType('');
        setParentesco('');
        setStartDate(new Date().toISOString().split('T')[0]);

        // If initial block is provided, fetch units for that block
        if (initialBlockId) {
          onBlockChange(initialBlockId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingResident, initialBlockId, initialUnitId]);

  // Reset confirmation modal when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowConfirmModal(false);
      setDisclaimerAcknowledged(false);
      setPendingFormData(null);
      // Reset ESC counter
      setEscPressCount(0);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
        escTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Handle ESC key - requires double-press to close
  useEffect(() => {
    if (!isOpen || isLoading || isActuallySubmitting) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();

        if (escPressCount === 0) {
          // First ESC press
          setEscPressCount(1);
          toast('Pressione ESC novamente para fechar', {
            duration: 2000,
          });

          // Reset counter after 3 seconds
          if (escTimeoutRef.current) clearTimeout(escTimeoutRef.current);
          escTimeoutRef.current = setTimeout(() => {
            setEscPressCount(0);
          }, 3000);
        } else {
          // Second ESC press - close modal
          setEscPressCount(0);
          if (escTimeoutRef.current) {
            clearTimeout(escTimeoutRef.current);
            escTimeoutRef.current = null;
          }
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
      }
    };
  }, [isOpen, onClose, escPressCount, isLoading, isActuallySubmitting]);

  // Auto-focus first required empty field when modal opens.
  // Uses preventScroll to avoid triggering scroll-related compositing on Windows/Electron.
  // Delay increased to 300ms to let the portal finish rendering before focus request.
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      const requiredFields = document.querySelectorAll('input[required], input[aria-required="true"]');
      for (const field of Array.from(requiredFields)) {
        const input = field as HTMLInputElement;
        if (!input.value || input.value.trim() === '') {
          input.focus({ preventScroll: true });
          break;
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen]);

  // Update form values when user is selected
  useEffect(() => {
    if (selectedUser) {
      if (selectedUser.firstName) setValue('first_name', forceUpper ? selectedUser.firstName.toUpperCase() : selectedUser.firstName);
      if (selectedUser.lastName) setValue('last_name', forceUpper ? selectedUser.lastName.toUpperCase() : selectedUser.lastName);
      if (selectedUser.email) setValue('email', selectedUser.email);
    }
  }, [selectedUser, setValue]);

  const handleBlockChangeInternal = (blockId: string) => {
    setSelectedBlockId(blockId);
    setSelectedUnitId('');
    onBlockChange(blockId);
  };

  const handleFormSubmit = async (data: ResidentFormData) => {
    // Validate: If both block and unit are selected, relation type is required
    if (selectedBlockId && selectedUnitId && !relationType) {
      toast.error('Tipo de relação é obrigatório quando bloco e unidade são selecionados');
      return;
    }

    // Validate required photos (only for new residents)
    if (!editingResident) {
      const photoReqs = fieldConfig?.photo_requirements ?? {};
      if (photoReqs.identity_photo && !photo) {
        toast.error('Foto de Identificação é obrigatória');
        return;
      }
      if (photoReqs.document_front && !documentFrontPhoto) {
        toast.error('Foto do Documento (Frente) é obrigatória');
        return;
      }
      if (photoReqs.document_back && !documentBackPhoto) {
        toast.error('Foto do Documento (Verso) é obrigatória');
        return;
      }
    }

    // Only show confirmation for new residents, not edits
    if (!editingResident) {
      setPendingFormData(data);
      setShowConfirmModal(true);
      setDisclaimerAcknowledged(false);
    } else {
      // For edits, submit directly
      await handleActualSubmit(data);
    }
  };

  // Handle confirmation modal confirm
  const handleConfirmSubmit = async () => {
    if (!pendingFormData || isActuallySubmitting) return;

    setIsActuallySubmitting(true);

    try {
      await handleActualSubmit(pendingFormData);
      setPendingFormData(null);
      setDisclaimerAcknowledged(false);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Resident registration failed:', error);
      toast.error('Erro ao cadastrar morador. Tente novamente.');
    } finally {
      setIsActuallySubmitting(false);
    }
  };

  // Handle confirmation modal cancel
  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingFormData(null);
    setDisclaimerAcknowledged(false);
  };

  const handleActualSubmit = async (data: ResidentFormData) => {
    const unitAssignment = selectedUnitId && relationType
      ? { unitId: selectedUnitId, relationType, parentesco: parentesco || undefined, startDate }
      : null;

    await onSubmit({
      formData: data,
      photo,
      documentFrontPhoto,
      documentBackPhoto,
      vehiclePhoto,
      vehicleList,
      note: newResidentNote,
      selectedUser,
      unitAssignment,
    });
  };

  const handleClose = () => {
    setPhoto(null);
    setDocumentFrontPhoto(null);
    setDocumentBackPhoto(null);
    setVehiclePhoto(null);
    setVehicleList([]);
    setShowVehicleForm(false);
    setNewVehiclePlate('');
    setNewVehicleModel('');
    setNewVehicleColor('');
    setNewResidentNote('');
    setShowAllPhotosModal(false);
    setAllPhotosModalColumn(null);
    setSelectedUser(null);
    setSelectedBlockId('');
    setSelectedUnitId('');
    setRelationType('');
    setParentesco('');
    reset();
    onClose();
  };

  return (
    <>
      <RegistrationModal
        isOpen={isOpen}
        onClose={handleClose}
        title={editingResident ? 'Editar Morador' : 'Novo Morador'}
        subtitle={editingResident ? 'Atualize as informações do morador' : 'Preencha os dados do novo morador'}
        onSubmit={handleSubmit(handleFormSubmit)}
        isLoading={isLoading}
        submitLabel={editingResident ? 'Atualizar' : 'Criar Morador'}
        darkMode={darkMode}
        embedded={embedded}
      photoSection={
        !editingResident ? (
          <div className="space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between">
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Foto</p>
              <button
                type="button"
                onClick={() => { setAllPhotosModalColumn(null); setShowAllPhotosModal(true); }}
                className={`text-[10px] font-medium px-2 py-0.5 rounded-md transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}
              >
                Editar todas
              </button>
            </div>

            {/* Identity photo */}
            <PhotoSection
              photo={photo}
              onPhotoCapture={setPhoto}
              onPhotoClear={() => setPhoto(null)}
              title={`Identificação${fieldConfig?.photo_requirements?.identity_photo ? ' *' : ''}`}
              disabled={isLoading}
              darkMode={darkMode}
              customerId={customerId}
            />

            {/* Doc + Vehicle thumbnails — each opens the live capture, same as Identificação
                above. "Editar todas" still opens the side-by-side grid. */}
            <div className="grid grid-cols-3 gap-1">
              {([
                { key: 'document_front', reqKey: 'document_front', p: documentFrontPhoto, set: setDocumentFrontPhoto },
                { key: 'document_back',  reqKey: 'document_back',  p: documentBackPhoto,  set: setDocumentBackPhoto },
                { key: 'vehicle',        reqKey: null,             p: vehiclePhoto,       set: setVehiclePhoto },
              ] as const).map(({ key, reqKey, p, set }) => (
                <PhotoCaptureThumb
                  key={key}
                  type={key}
                  photo={p}
                  onCapture={set}
                  required={reqKey ? !!(fieldConfig?.photo_requirements?.[reqKey]) : false}
                  disabled={isLoading}
                  darkMode={darkMode}
                  customerId={customerId}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className={`w-32 h-40 rounded-xl border-4 flex items-center justify-center ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
            }`}>
              <span className={`text-xs text-center px-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Foto editável na página de detalhes
              </span>
            </div>
          </div>
        )
      }
    >
      {/* Personal Info Section */}
      <PersonalInfoSection
        register={register}
        control={control}
        trigger={trigger}
        setValue={setValue}
        errors={errors}
        disabled={isLoading}
        cpfRequired={isCpfRequired}
        darkMode={darkMode}
        fieldConfig={fieldConfig}
        customerId={customerId}
      />

      {/* Unit Assignment Section - Only for new residents (MOVED ABOVE UserLinkingSection) */}
      {!editingResident && (
        <UnitAssignmentSection
          blocks={blocks}
          units={units}
          selectedBlockId={selectedBlockId}
          selectedUnitId={selectedUnitId}
          relationType={relationType}
          parentesco={parentesco}
          startDate={startDate}
          onBlockChange={handleBlockChangeInternal}
          onUnitChange={setSelectedUnitId}
          onRelationTypeChange={setRelationType}
          onParentescoChange={setParentesco}
          onStartDateChange={setStartDate}
          disabled={isLoading}
          darkMode={darkMode}
          condoType={condoType as any}
          customerId={customerId}
        />
      )}

      {/* User Linking Section - Only for new residents */}
      {!editingResident && (
        <UserLinkingSection
          selectedUser={selectedUser}
          onUserSelect={setSelectedUser}
          disabled={isLoading}
          darkMode={darkMode}
        />
      )}

      {/* Vehicle - Only for new residents, same inline-add pattern as visitor registration */}
      {!editingResident && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`text-[11px] font-semibold tracking-widest uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Veículo
            </label>
            {!showVehicleForm && vehicleList.length === 0 && (
              <button
                type="button"
                onClick={() => setShowVehicleForm(true)}
                disabled={isLoading}
                className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                  darkMode
                    ? 'border-primary-500 text-primary-400 hover:bg-primary-900/30'
                    : 'border-primary-600 text-primary-700 hover:bg-primary-50'
                }`}
              >
                <Plus className="h-3.5 w-3.5" />
                Adicionar Veículo
              </button>
            )}
          </div>

          {showVehicleForm && (
            <div className={`rounded-xl border p-3 mb-2 ${darkMode ? 'bg-gray-700/50 border-gray-500' : 'bg-white border-gray-300 shadow-sm'}`}>
              <div className="flex gap-3 items-end">
                <div className="flex-1 min-w-0">
                  <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Placa *</label>
                  <input
                    type="text"
                    value={newVehiclePlate}
                    onChange={(e) => setNewVehiclePlate(e.target.value.toUpperCase())}
                    placeholder="ABC1D23"
                    maxLength={8}
                    autoFocus
                    className={`block w-full rounded-lg py-2 px-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Modelo</label>
                  <input
                    type="text"
                    value={newVehicleModel}
                    onChange={(e) => setNewVehicleModel(e.target.value)}
                    placeholder="Ex: Civic"
                    className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cor</label>
                  <input
                    type="text"
                    value={newVehicleColor}
                    onChange={(e) => setNewVehicleColor(e.target.value)}
                    placeholder="Ex: Preto"
                    className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => { setShowVehicleForm(false); setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor(''); }}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${darkMode ? 'border-gray-500 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const plate = newVehiclePlate.trim();
                      if (!plate) return;
                      setVehicleList([{ plate, model: newVehicleModel.trim(), color: newVehicleColor.trim() }]);
                      setShowVehicleForm(false);
                      setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor('');
                    }}
                    disabled={!newVehiclePlate.trim()}
                    className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {vehicleList.length > 0 && (
            <div className={`rounded-xl border overflow-hidden divide-y ${darkMode ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
              {vehicleList.map((v, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-3 ${darkMode ? 'bg-gray-800/30' : 'bg-white'}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Car className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <LicensePlate plate={v.plate} size="sm" />
                    {(v.model || v.color) && (
                      <p className={`text-xs mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{[v.model, v.color].filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setVehicleList([])}
                    className={`p-1 rounded transition-colors flex-shrink-0 ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-gray-200'}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {vehicleList.length === 0 && !showVehicleForm && (
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum veículo adicionado</p>
          )}
        </div>
      )}

      {/* Internal note — Only for new residents, same pattern as VisitorRegistrationModal */}
      {!editingResident && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <MessageSquare className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <label className={`text-[11px] font-semibold tracking-widest uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Nota Interna
            </label>
            <span className={`text-[10px] ml-auto ${newResidentNote.length > 0 ? (darkMode ? 'text-gray-400' : 'text-gray-500') : (darkMode ? 'text-gray-600' : 'text-gray-400')}`}>
              {newResidentNote.length}/500
            </span>
          </div>
          <textarea
            value={newResidentNote}
            onChange={(e) => setNewResidentNote(e.target.value)}
            placeholder="Anotação interna sobre este morador..."
            maxLength={500}
            disabled={isLoading}
            className={`w-full rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 p-2.5 min-h-[80px] disabled:opacity-50 ${
              darkMode
                ? 'bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500/50'
                : 'bg-white border border-gray-300 text-gray-700 placeholder-gray-400 focus:border-blue-400'
            }`}
          />
          <p className={`text-[10px] mt-1.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Visível apenas para operadores
          </p>
        </div>
      )}
    </RegistrationModal>

      {/* All Photos Edit Modal */}
      <AllPhotosEditModal
        open={showAllPhotosModal}
        onClose={() => { setShowAllPhotosModal(false); setAllPhotosModalColumn(null); }}
        onSave={(photos) => {
          if (photos.identity) setPhoto(photos.identity as any);
          if (photos.document_front) setDocumentFrontPhoto(photos.document_front);
          if (photos.document_back) setDocumentBackPhoto(photos.document_back);
          if (photos.vehicle) setVehiclePhoto(photos.vehicle);
        }}
        currentPhotos={{
          identity: photo ? { base64: (photo as any).base64 || '', blob: photo.blob, width: 0, height: 0, faceDetected: false } : null,
          document_front: documentFrontPhoto,
          document_back: documentBackPhoto,
          vehicle: vehiclePhoto,
        }}
        columns={allPhotosModalColumn ? [allPhotosModalColumn] : undefined}
        customerId={customerId}
        darkMode={darkMode}
      />

      {/* Registration Confirmation Modal — portal to #modal-root (same as RegistrationModal) */}
      {showConfirmModal && ReactDOM.createPortal(
        <div
          className="absolute inset-0 bg-black bg-opacity-75 overflow-y-auto h-full w-full z-[60] flex items-center justify-center"
          onClick={handleCancelConfirm}
        >
          <div
            className={`relative rounded-xl shadow-2xl w-full max-w-md mx-4 border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading overlay for confirmation modal */}
            <FormLoadingOverlay
              isLoading={isActuallySubmitting}
              message="Cadastrando morador..."
              darkMode={darkMode}
            />

            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            } flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-400" />
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Confirmar Cadastro de Morador
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <p className={`text-base leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Você está prestes a cadastrar um novo morador no sistema.
              </p>

              {/* Disclaimer Section */}
              <div className={`${
                darkMode ? 'bg-amber-900/20 border-amber-600/50' : 'bg-amber-50 border-amber-300'
              } border rounded-lg p-4`}>
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className={`h-5 w-5 ${
                    darkMode ? 'text-amber-300' : 'text-amber-600'
                  } flex-shrink-0 mt-0.5`} />
                  <h4 className={`text-sm font-bold ${
                    darkMode ? 'text-amber-300' : 'text-amber-700'
                  } uppercase tracking-wide`}>
                    AVISO DE RESPONSABILIDADE
                  </h4>
                </div>
                <div className={`text-xs ${
                  darkMode ? 'text-amber-200' : 'text-amber-900'
                } leading-relaxed space-y-2`}>
                  <p>Ao cadastrar este morador, você declara que:</p>
                  <ul className="space-y-1 ml-3">
                    <li>• Verificou a identidade e autorização da pessoa</li>
                    <li>• Confirma que as informações fornecidas são verdadeiras</li>
                    <li>• Está ciente que este cadastro será registrado em seu nome</li>
                    <li>• Assume responsabilidade pela veracidade dos dados</li>
                  </ul>
                  <p className="mt-3">
                    Este cadastro será registrado permanentemente vinculado ao seu usuário{' '}
                    <span className={`font-semibold ${
                      darkMode ? 'text-cyan-300' : 'text-cyan-700'
                    }`}>({operatorName})</span>.
                  </p>
                  <p className={`text-xs mt-2 ${
                    darkMode ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    Registro será criado em: {new Date().toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </p>
                </div>
                <div className={`mt-4 pt-3 ${
                  darkMode ? 'border-t border-gray-600' : 'border-t border-amber-200'
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={disclaimerAcknowledged}
                      onChange={(e) => setDisclaimerAcknowledged(e.target.checked)}
                      className={`mt-0.5 h-4 w-4 rounded cursor-pointer ${
                        darkMode
                          ? 'border-gray-500 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0'
                          : 'border-gray-400 bg-white text-blue-600 focus:ring-2 focus:ring-blue-500'
                      }`}
                    />
                    <span className={`text-xs ${
                      darkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
                    } transition-colors`}>
                      Li e concordo com os termos de responsabilidade do cadastro
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 rounded-b-xl flex justify-end gap-3 ${
              darkMode ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
              <button
                onClick={handleCancelConfirm}
                disabled={isActuallySubmitting}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600'
                    : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={!disclaimerAcknowledged || isActuallySubmitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isActuallySubmitting ? 'Cadastrando...' : 'Confirmar Cadastro'}
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('modal-root') ?? document.body
      )}
    </>
  );
};

export default ResidentRegistrationModal;
